<template>
  <div class="full-height">
    <div class="position--relative">
      <transition name="fade">
        <v-progress-linear
          v-if="isLoading"
          class="progress-linear--absolute"
          color="warning"
          height="3"
          indeterminate
        />
      </transition>
    </div>

    <transition name="fade">
      <div v-if="isSuccess" class="full-height">
        <div v-if="isValidArray(items)" class="table">
          <v-layout class="table__header" align-center>
            <v-flex class="table__checkbox" xs1>
              <v-checkbox
                v-if="hasRole('financial', 'ROLE_PUT_ORDER_REPROCESS_MASSIVE') && isValidArray(items)"
                v-model="checkedAllItems"
                :disabled="!selectedCompany"
                hide-details
                @change="handleCheckedAllItems"
              />
            </v-flex>
            <v-flex xs2>{{ $t(`${namespace}.billingId`) }}</v-flex>
            <v-flex xs2>{{ $t(`${namespace}.price`) }}</v-flex>
            <v-flex xs2>{{ $t(`${namespace}.dateClosed`) }}</v-flex>
            <v-flex xs2>CNPJ / {{ $t(`${namespace}.companyId`) }}</v-flex>
            <v-flex xs3>{{ $t(`${namespace}.companyName`) }}</v-flex>
          </v-layout>

          <div class="table__status">
            <strong>{{ firstItemNumber }}</strong>-<strong>{{ lastItemNumber }}</strong> {{ $t('Common.of') }} <strong>{{ pagination.totalElements | formatNumber }}</strong>&nbsp;{{ $t(`${namespace}.invoicePaginateText`) }}
          </div>

          <v-list subheader two-line class="table__body">
            <div v-for="(item, i) in items" :key="`tr-${i}`">
              <v-list-tile
                class="table__body__tr"
                :class="[
                  { 'table__body__tr--active': activateLine(item) },
                  { 'table__body__tr--disabled': item.id === targetTr }
                ]"
              >
                <v-list-tile-content>
                  <v-list-tile-sub-title>
                    <v-layout align-center>
                      <v-flex class="table__checkbox table__td--prevent" xs1>
                        <v-checkbox
                          v-if="hasRole('financial', 'ROLE_PUT_ORDER_REPROCESS_MASSIVE')"
                          v-model="selectedItems"
                          :value="item.id"
                          hide-details
                          :disabled="Boolean(item.id === targetTr)"
                          @change="handleSelectedItems"
                        />

                        <v-tooltip v-if="hasRole('financial', 'ROLE_PUT_ORDER_REPROCESS_MASSIVE')" top>
                          <template v-slot:activator="{ on }">
                            <v-btn
                              v-on="on"
                              class="ma-0"
                              color="gray-6"
                              :disabled="Boolean(item.id === targetTr)"
                              flat
                              icon
                              small
                              @click="handleReprocessThisInvoice(item.id)"
                            >
                              <v-progress-circular v-if="item.id === targetTr" indeterminate color="accent" size="15" width="2" />
                              <v-icon v-else>cached</v-icon>
                            </v-btn>
                          </template>
                          <span>{{ $t(`${namespace}.reprocessThisInvoice`) }}</span>
                        </v-tooltip>
                      </v-flex>

                      <v-flex xs2>
                        <span class="text--ellipsis">{{ item.numericalId }}</span>
                      </v-flex>

                      <v-flex xs2>
                        <span class="text--ellipsis">{{ item.amount | currencyBRLwithoutSymbol }}</span>
                      </v-flex>

                      <v-flex xs2>
                        <span class="text--ellipsis">{{ item.dueDate | formatDate('DD/MM/YYYY') }}</span>
                      </v-flex>

                      <v-flex xs2>
                        <span class="text--ellipsis">{{ item.document | cnpj }}</span>
                        <span class="gray-6--text caption text--ellipsis">{{ item.companyId }}</span>
                      </v-flex>

                      <v-flex xs3>
                        <span class="text--ellipsis">{{ item.companyName }}</span>
                      </v-flex>
                    </v-layout>
                  </v-list-tile-sub-title>
                </v-list-tile-content>
              </v-list-tile>

              <v-divider />
            </div>
          </v-list>
        </div>

        <EmptyState
          v-else
          image="usage-policy"
          :text="$t(`${namespace}.noneInvoicesFound`)"
        />

        <div v-if="pagination.totalElements > pagination.itemsPerPage" class="text-xs-center">
          <v-container class="px-0">
            <v-pagination
              v-model="pagination.currentPage"
              color="accent"
              :total-visible="totalVisible"
              :length="pagination.totalPages"
              @input="handlePage"
            />
          </v-container>
        </div>
      </div>
    </transition>

    <transition name="fade">
      <v-layout v-if="isFailed" align-center justify-center column fill-height>
        {{ $t('Common.genericErrorMsg') }}<br>
        <a
          :title="$t('Common.tryAgain')"
          class="accent--text"
          @click.prevent="handleReloadData"
        >
          {{ $t('Common.tryAgain') }}.
        </a>
      </v-layout>
    </transition>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { STATUS_IS_LOADING, STATUS_SUCCESS, STATUS_FAILED } from 'utils/constants'
import { hasRole, isArray, formatNumber } from 'utils/functions'
import EmptyState from 'components/EmptyState'

export default {
  name: 'Table',
  props: {
    items: {
      type: Array,
      required: true
    },
    pagination: {
      type: Object,
      required: true
    },
    status: {
      type: Number,
      default: STATUS_IS_LOADING
    },
    clearSelectedItems: {
      type: Boolean
    }
  },
  components: {
    EmptyState
  },
  computed: {
    ...mapState({
      selectedCompany: ({ company }) => company.selectedCompany,
      claims: ({ userClaims }) => userClaims.claims
    }),

    totalVisible () {
      return window.innerWidth <= 420 ? 5 : 7
    },

    firstItemNumber () {
      const { currentPage, itemsPerPage } = this.pagination
      const calc = (currentPage * itemsPerPage) - itemsPerPage + 1
      const item = currentPage === 1 ? 1 : calc
      return formatNumber(item)
    },

    lastItemNumber () {
      const { currentPage, itemsPerPage, totalElements } = this.pagination
      const calc = currentPage * itemsPerPage
      const item = calc > totalElements ? totalElements : calc
      return formatNumber(item)
    },

    isLoading () {
      return this.status === STATUS_IS_LOADING
    },

    isSuccess () {
      return this.status === STATUS_SUCCESS
    },

    isFailed () {
      return this.status === STATUS_FAILED
    }
  },
  data () {
    return {
      namespace: 'Views.ManualRetrieval',
      selectedItem: null,
      checkedAllItems: false,
      selectedItems: [],
      targetTr: null
    }
  },
  methods: {
    hasRole (group, role) {
      return hasRole(group, role)
    },

    isValidArray (val) {
      return isArray(val)
    },

    handlePage (page) {
      this.$emit('goToPage', page)
    },

    handleReloadData () {
      this.$emit('reloadData')
    },

    activateLine (item) {
      return item.id === this.selectedItem || this.selectedItems.find(i => i === item.id)
    },

    handleCheckedAllItems (val) {
      this.selectedItems = val
        ? [...this.items].map(item => item.id)
        : []
    },

    handleSelectedItems (val) {
      this.checkedAllItems = this.selectedItems.length === this.items.length
    },

    handleReprocessThisInvoice (val) {
      this.targetTr = val
      this.$emit('reprocessThisInvoice', val)
    }
  },
  watch: {
    selectedItems (val) {
      this.$emit('selectedItems', val)
    },

    clearSelectedItems () {
      this.selectedItems = []
      this.checkedAllItems = false
    },

    status (val) {
      if (val > 1) this.targetTr = null
    }
  }
}
</script>
